import {
  select,
  all,
  call,
  take,
  takeEvery,
  takeLatest,
  put,
  throttle,
} from 'redux-saga/effects';
import utils from '../../utils';
import actions from '../actions';
import selectors from '../selectors';
import channels from './channels/segmentation';

/**
 * Initializes the segmentation tree
 * @param {*} controller - contains methods to manipulate the model, viewer and segmentation tree.
 * @param {*} action - payloads of information that send data from the store
 */
function* initializeSegmentationSaga(controller, action) {
  const { order, opts } = action.payload;
  if (order) {
    controller.initSegmentationForOrder(order, opts);
  } else {
    controller.initSegmentation(opts);
  }
  const nodes = utils.getNodeMap();
  yield put(actions.segmentation.loadNodes(nodes));
  yield put(actions.segmentation.ready());
}

/**
 * Update a node in the segmentation tree
 * @param {*} controller - contains methods to manipulate the model, viewer and segmentation tree.
 * @param {*} action - payloads of information that send data from the store
 *
 */
function* updateNodeSaga(controller, action) {
  const {
    payload: { nodes, properties },
  } = action;
  const refs = Array.isArray(nodes) ? nodes : [nodes];
  const opacity = yield select(selectors.model.getFacadeOpacity);
  refs.forEach((ref) => utils.updateNodeProperties(ref, properties));
  controller.repaintNodeOpacity(refs[0], opacity);
}

/**
 * Creates a window package
 * @param {*} controller - contains methods to manipulate the model, viewer and segmentation tree.
 * @param {object} properties - properties perameters for the current node
 * @param {string} parentID - uuid of the parent node
 */
function* createWindowPackage(
  controller,
  { payload: { properties, parentId } },
) {
  const newPackageId = utils.createWindowPackage(parentId, properties);
  const opacity = yield select(selectors.model.getFacadeOpacity);
  controller.repaintNodeOpacity(parentId, opacity);
  yield put(actions.segmentation.setNodeSelection([newPackageId]));
}

/**
 * Change a split node's visibility
 * @param {string} uuid - node uuid value
 */
function* toggleSplitSaga({ payload: { uuid } }) {
  const nodes = yield select(selectors.segmentation.getNodes);
  const node = nodes[uuid];
  const {
    properties: {
      present: { value: presence },
    },
  } = node;
  const present = !presence;
  yield put(actions.segmentation.updateNode(uuid, { present }));
  utils.addSplitLevelTo(node.uuid);
}

/**
 * Change a node on the segmentation tree
 * @param {string} uuid - node uuid value
 * @param {string} type - the node classification
 * @param {object} properties - the property objects for a node
 */
function* nodeChangedSaga({ node: { uuid, type, properties } }) {
  yield put(
    actions.segmentation.nodeChanged(
      uuid,
      type,
      utils.propertiesArrayToPropertiesMap(properties),
    ),
  );
}

/**
 * Add a node to the segmentation tree
 * @param {object} node - a new node for the segmentation tree
 */
function* nodeAddedSaga({ node }) {
  yield put(actions.segmentation.nodeAdded(node));
}

/**
 * Remove a node from the segmentation tree
 * @param {object} node - an existing node on the segmentation tree
 * @param {object} parentNode - the direct ascendent of the node you want to delete (aka node.parent)
 */
function* nodeRemovedSaga({ node, parentNode }) {
  yield put(actions.segmentation.nodeRemoved(node, parentNode));
}

/**
 * Initialize channels for when a node is changed
 * @param {*} controller - contains methods to manipulate the model, viewer and segmentation tree.
 */
function* initNodeChangedSaga(controller) {
  window.ctrl = controller;
  let chan;
  try {
    yield take(actions.segmentation.ready);
    chan = [
      yield call(
        channels.nodeChangedChannel,
        controller.model.segmentationTree,
      ),
      yield call(channels.nodeAddedChannel, controller.model.segmentationTree),
      yield call(
        channels.nodeRemovedChannel,
        controller.model.segmentationTree,
      ),
    ];
    yield takeEvery(chan[0], nodeChangedSaga);
    yield takeEvery(chan[1], nodeAddedSaga);
    yield takeEvery(chan[2], nodeRemovedSaga);
    yield take(actions.segmentation.teardown);
  } finally {
    chan.forEach((c) => c.close());
  }
}

/**
 * Set the default Opacity for all facades
 * @param {*} controller - contains methods to manipulate the model, viewer and segmentation tree.
 * @param {number} facadeOpacity - the opacity of the facade
 */
function setDefaultOpacityForFacadesSaga(
  controller,
  { payload: { facadeOpacity } },
) {
  controller.setOpacityForElements(controller.model.elements, facadeOpacity);
  controller.updateRender();
}

/**
 * Main Saga function for Segmentation
 * @param {*} controller - contains methods to manipulate the model, viewer and segmentation tree.
 */
export default function* sagas(controller) {
  yield all([
    yield takeEvery(
      actions.segmentation.updateNode,
      updateNodeSaga,
      controller,
    ),
    yield takeEvery(actions.segmentation.toggleSplit, toggleSplitSaga),
    yield takeLatest(
      actions.segmentation.init,
      initializeSegmentationSaga,
      controller,
    ),
    yield takeLatest(
      actions.segmentation.init,
      initNodeChangedSaga,
      controller,
    ),
    yield takeLatest(
      actions.segmentation.createWindowPackage,
      createWindowPackage,
      controller,
    ),
    yield throttle(
      32,
      actions.model.setDefaultOpacityForFacades,
      setDefaultOpacityForFacadesSaga,
      controller,
    ),
  ]);
}
